import React, { useState } from 'react';
import { Search, ExternalLink, FileText, Wrench, Shield, Star } from 'lucide-react';
import { GenerationSettings } from '../types/index.ts';
import { logger } from '../lib/logger.ts';
import { apiV1Url } from '../lib/apiBaseUrl.ts';
import { creatorFetch } from '../services/creatorFetch.ts';

interface SearchResult {
  title: string;
  url: string;
  snippet: string;
  display_url: string;
  category: string;
  relevance_score: number;
  source_type: string;
}

interface SearchResults {
  product_name: string;
  query: string;
  how_to_use: SearchResult[];
  how_to_repair: SearchResult[];
  total_results: number;
  search_time_ms: number;
}

interface ProductSearchProps {
  onSearchResults: (results: SearchResults) => void;
  onBack: () => void;
  generationSettings: GenerationSettings;
}

const ProductSearch: React.FC<ProductSearchProps> = ({
  onSearchResults,
  onBack,
  generationSettings
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const intentLabel = generationSettings.template === 'quickstart' ? 'Quickstart' : 'Product Guide';

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      logger.debug('Starting product search', { searchQuery });

      const response = await creatorFetch(apiV1Url('/api/v1/search-product'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_name: searchQuery,
          max_results: 5
        })
      });

      logger.debug('Search response status', { status: response.status });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Search failed: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      logger.debug('Search response data', data);

      if (data.success) {
        setSearchResults(data.data);
        logger.debug('Search completed successfully');
      } else {
        throw new Error(data.message || 'Search failed');
      }
    } catch (err) {
      logger.error('Search error', err);
      setError(err instanceof Error ? err.message : 'An error occurred during search');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseSelected = () => {
    if (searchResults) {
      onSearchResults(searchResults);
    }
  };

  const getSourceIcon = (sourceType: string) => {
    switch (sourceType) {
      case 'manufacturer':
        return <Shield className="w-4 h-4 text-blue-600" />;
      case 'manual':
        return <FileText className="w-4 h-4 text-green-600" />;
      case 'repair':
        return <Wrench className="w-4 h-4 text-orange-600" />;
      default:
        return <Search className="w-4 h-4 text-gray-600" />;
    }
  };

  const getSourceBadge = (sourceType: string) => {
    const colors = {
      manufacturer: 'bg-blue-100 text-blue-800',
      manual: 'bg-green-100 text-green-800',
      repair: 'bg-orange-100 text-orange-800',
      forum: 'bg-purple-100 text-purple-800',
      general: 'bg-gray-100 text-gray-800'
    };
    return colors[sourceType as keyof typeof colors] || colors.general;
  };

  const renderSearchResults = (results: SearchResult[], category: string) => {
    if (results.length === 0) return null;

    return (
      <div className="simpi-grid">
        {results.map((result, index) => (
          <div key={`${category}-${index}`} className="simpi-item">
            <div className="simpi-card-wrapper">
              <div className="simpi-card">
                <div className="simpi-card-text">
                  <div className="flex items-start space-x-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-2">
                        {getSourceIcon(result.source_type)}
                        <h3 className="title text-blue-600 hover:text-blue-800">
                          {result.title}
                        </h3>
                      </div>

                      <p className="brand mb-2">{result.snippet}</p>

                      <div className="flex items-center space-x-2 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getSourceBadge(result.source_type)}`}>
                          {result.source_type}
                        </span>
                        <div className="flex items-center space-x-1 text-gray-500">
                          <Star className="w-3 h-3" />
                          <span>{(result.relevance_score * 100).toFixed(0)}% relevance</span>
                        </div>
                        <a
                          href={result.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink className="w-3 h-3" />
                          <span className="text-xs">{result.display_url}</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Search Online for {intentLabel} Sources</h2>
        <p className="text-gray-600">
          {generationSettings.template === 'quickstart'
            ? 'Find product pages, manuals, and usage guidance that help a new user get started quickly'
            : 'Find manuals, documentation, and broader how-to material for any product'}
        </p>
      </div>

      {/* Search Interface */}
      <div className="flex space-x-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Enter product name (e.g., iPhone 14, Samsung Galaxy S23, Dell XPS 13)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            disabled={isLoading}
          />
        </div>
        <button
          onClick={handleSearch}
          disabled={isLoading || !searchQuery.trim()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
        >
          <Search className="w-5 h-5" />
          <span>{isLoading ? 'Searching...' : 'Search'}</span>
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Searching for product information...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2 text-red-800">
            <Search className="w-5 h-5" />
            <span className="font-medium">Search Error</span>
          </div>
          <p className="text-red-700 mt-2">{error}</p>
          <button
            onClick={handleSearch}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Search Results */}
      {searchResults && !isLoading && (
        <div className="space-y-6">
          <div className="text-center">
            <h3 className="text-lg font-semibold mb-2">
              Search Results for "{searchResults.product_name}"
            </h3>
            <p className="text-gray-600">
              Found {searchResults.total_results} results
            </p>
          </div>

          {searchResults.how_to_use.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-blue-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">How to Use</h4>
                  <span className="text-sm text-blue-700">
                    ({searchResults.how_to_use.length} results)
                  </span>
                </div>
              </div>
              <div className="p-6">
                {renderSearchResults(searchResults.how_to_use, 'how_to_use')}
              </div>
            </div>
          )}

          {searchResults.how_to_repair.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="bg-orange-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <Wrench className="w-5 h-5 text-orange-600" />
                  <h4 className="font-semibold text-orange-900">How to Repair</h4>
                  <span className="text-sm text-orange-700">
                    ({searchResults.how_to_repair.length} results)
                  </span>
                </div>
              </div>
              <div className="p-6">
                {renderSearchResults(searchResults.how_to_repair, 'how_to_repair')}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-between items-center pt-4">
            <button
              onClick={onBack}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Back
            </button>

            <button
              onClick={handleUseSelected}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Use Search Results
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;